<script setup lang="ts">
import { Ref, ref, toRefs, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import {
	numericAndFloat,
	required,
	titleInputRules,
} from "@/ui/validation/validationRules";
import BexTodoDO = Modules.BookingExperts.Data.BexTodoDO;
import { toastApiResult } from "@/toaster";
import { useApiClient } from "@/api/client";
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;
import BexPriceListDTO = Modules.BookingExperts.Data.BexPriceListDTO;
const isDialogVisible = defineModel<boolean>();
const emit = defineEmits(["refresh-todos"]);

const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		bexTodo: BexTodoDO;
	}>(),
	{}
);

const availableDueTypes = [
	"before_arrival",
	"on_arrival",
	"on_departure",
	"after_departure",
];

const { bexTodo } = toRefs(props);
const isLoading = ref<boolean>(true);
const localBexToDo = ref<BexTodoDO>();

onBeforeMount(() => (localBexToDo.value = { ...bexTodo.value }));

const isFormValid: Ref<boolean> = ref(false);

const saveTodo = () => {
	if (localBexToDo.value.uuid) {
		updateExistingTodo();
	} else {
		createNewTodo();
	}
};

const updateExistingTodo = async () => {
	const response = await toastApiResult(
		useApiClient("proLoyalty").updateTodo(localBexToDo.value)
	);
	if (response.isOk()) {
		emit("refresh-todos");
		isDialogVisible.value = false;
	}
};

const createNewTodo = async () => {
	const response = await toastApiResult(
		useApiClient("proLoyalty").createTodo(localBexToDo.value)
	);
	if (response.isOk()) {
		emit("refresh-todos");
		isDialogVisible.value = false;
	}
};

const administrations = ref<BexAdministrationDO[]>([]);
const priceLists = ref<Record<number, BexPriceListDTO[]>>({});

const fetchAdministrations = async () => {
	const response = await useApiClient("bookingExperts").getAdministrations();
	if (!response.isOk()) return;

	administrations.value = response.value;

	const promises = response.value.map((administration) => {
		return useApiClient("proLoyalty")
			.getTaskLists(administration.id)
			.then((res) => ({
				id: administration.id,
				result: res,
			}));
	});

	const results = await Promise.all(promises);

	results.forEach(({ id, result }) => {
		priceLists.value[id] = result.isOk()
			? [{ id: null, title: "None" }, ...result.value]
			: [{ id: null, title: "None" }];
		if (!bexTodo.value?.administration_todo_lists?.[id]) {
			bexTodo.value.administration_todo_lists[id] = null;
		}
	});
};

onBeforeMount(async () => {
	await fetchAdministrations();
	isLoading.value = false;
});
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="50vw">
		<v-form v-model="isFormValid">
			<v-card elevation="9" class="help" min-height="300px">
				<v-toolbar style="border-radius: 0 !important" flat>
					<v-toolbar-title
						><i class="fa-duotone fa-list-check fa-xl mr-3"></i>Task
					</v-toolbar-title>
					<v-spacer></v-spacer>

					<v-btn icon @click="isDialogVisible = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>

				<v-progress-linear
					v-if="isLoading"
					size="4"
					color="primary"
					indeterminate
				/>

				<template v-else>
					<v-divider style="border-color: unset !important" />
					<div class="mt-5">
						<div class="px-5">
							<h6 class="text-h6 mb-3">Name:</h6>
							<v-text-field
								hide-details="auto"
								:min-width="200"
								placeholder="Title name"
								v-model="localBexToDo.name"
								:rules="titleInputRules(t, { max: 50 })"
							/>
						</div>
					</div>

					<div class="mt-5">
						<div class="px-5">
							<h6 class="text-h6 mb-3">Details:</h6>
							<v-textarea
								v-model="localBexToDo.details"
								variant="filled"
								hide-details="auto"
								rows="3"
								placeholder="Optionally add some details"
							></v-textarea>
						</div>
					</div>

					<div class="mt-5">
						<div class="px-5">
							<h6 class="text-h6 mb-3">Quantity:</h6>
							<v-combobox
								width="150"
								v-model="localBexToDo.quantity"
								:items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
								:rules="[required(t), numericAndFloat(t)]"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</div>
					</div>

					<div class="mt-5">
						<div class="px-5 d-flex">
							<h6 class="text-h6 mb-3 mr-3">Task List:</h6>
							<span class="text-caption">(per Administration)</span>
						</div>
						<div
							v-for="administration in administrations"
							class="d-flex px-5 mt-3"
						>
							<div class="mt-3 mr-3">{{ administration.name }}:</div>
							<v-select
								v-model="bexTodo.administration_todo_lists[administration.id]"
								hide-details="auto"
								item-title="title"
								item-value="id"
								:items="priceLists[administration.id]"
							/>
						</div>
					</div>

					<div class="mt-5">
						<div class="px-5">
							<h6 class="text-h6 mb-3">Task is due:</h6>
							<v-select
								width="250"
								v-model="localBexToDo.due_type"
								:items="availableDueTypes"
								:item-title="(item) => t(`pro_loyalty.due_types.${item}`)"
								:item-value="(item) => item"
								:rules="[required(t)]"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</div>
					</div>

					<div class="mt-5">
						<div class="px-5">
							<div>
								<v-checkbox
									hide-details
									color="primary"
									label="This task should be checked after being finished"
									v-model="localBexToDo.is_checkable"
								/>
								<v-checkbox
									hide-details
									color="primary"
									label="This task is taxable after being checked"
									v-model="localBexToDo.is_taxable"
								/>
								<v-checkbox
									hide-details
									color="primary"
									label="This task blocks check-in"
									v-model="localBexToDo.is_blocker"
								/>
								<v-checkbox
									hide-details
									color="primary"
									label="Notify the executor of the task via SMS"
									v-model="localBexToDo.is_urgent"
								/>
							</div>
						</div>
					</div>

					<v-divider style="border-color: unset !important"></v-divider>

					<v-card-actions class="mx-5 my-2">
						<v-spacer></v-spacer>
						<v-btn
							text="Cancel"
							variant="text"
							color="error"
							@click="isDialogVisible = false"
						></v-btn>
						<v-btn
							color="primary"
							text="Store"
							variant="flat"
							:disabled="!isFormValid"
							@click="saveTodo()"
						></v-btn>
					</v-card-actions>
				</template>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<style lang="scss"></style>
